/**
 * Laddar om sidan när en lazy-laddad chunk saknas.
 *
 * Varje bygge ger nya filnamnshashar. Har någon portalen öppen när en deploy
 * går igenom sitter deras flik kvar med det gamla app-skalet, och första gången
 * de navigerar till en lazy-laddad vy — ÄTA, Kalkyl, Offert, Tidplan, i princip
 * allt utom inloggningen — begär den en chunk som inte finns längre.
 *
 * Servern svarar då med index.html för den saknade .js-filen, vilket ger
 * "Unexpected token '<'" följt av ChunkLoadError, och användaren får en vit
 * ruta. En omladdning hämtar nytt skal och löser det — men det vet inte kunden
 * eller IA:n som råkar ut för det.
 *
 * Vi gör omladdningen automatiskt i stället.
 *
 * ⚠️ Måste vara skyddad mot loopar. Är chunken borta av något ANNAT skäl än en
 * deploy — trasig uppladdning, CDN-miss, en fil som faktiskt saknas i bygget —
 * skulle en villkorslös omladdning ge en oändlig cykel av vita sidor. Därför
 * laddar vi om högst en gång per flik och tidsfönster; slår det inte får felet
 * synas som vanligt så det går att felsöka.
 */

const NYCKEL = 'chunkReloadAt';
const FONSTER_MS = 30_000;

const arChunkFel = (fel) => {
  if (!fel) return false;
  const namn = fel.name || '';
  const text = fel.message || String(fel);
  return namn === 'ChunkLoadError'
    || /Loading chunk \S+ failed/i.test(text)
    || /Loading CSS chunk \S+ failed/i.test(text);
};

const harLaddatNyligen = () => {
  try {
    const senast = Number(window.sessionStorage.getItem(NYCKEL) || 0);
    return senast > 0 && Date.now() - senast < FONSTER_MS;
  } catch {
    // sessionStorage kan kasta i privat läge eller med blockerade cookies.
    // Utan spärr vågar vi inte ladda om — hellre ett synligt fel än en loop.
    return true;
  }
};

const markeraOmladdning = () => {
  try {
    window.sessionStorage.setItem(NYCKEL, String(Date.now()));
  } catch {
    /* se kommentaren ovan */
  }
};

const hanteraChunkFel = (fel) => {
  if (!arChunkFel(fel)) return;
  if (harLaddatNyligen()) {
    console.error('Chunk saknas fortfarande efter omladdning — laddar inte om igen:', fel);
    return;
  }
  markeraOmladdning();
  window.location.reload();
};

/**
 * Kopplar in lyssnarna. Anropas en gång vid appstart.
 *
 * Två vägar behövs: dynamiska importer avvisar ett promise
 * (unhandledrejection), medan chunk-laddaren också kan kasta synkront i
 * webbläsarens felhanterare (error).
 */
export const aktiveraChunkOmladdning = () => {
  window.addEventListener('unhandledrejection', (e) => hanteraChunkFel(e.reason));
  window.addEventListener('error', (e) => hanteraChunkFel(e.error || e));
};

export default aktiveraChunkOmladdning;
